import { createReducer } from "@reduxjs/toolkit"
import { path, find, propEq, reject } from "ramda"

export const initialState = {
  currentPupil: null,
  isPupilLoading: false,
  isNoteLoading: false,
  isOutflowAdviceLoading: false,
  outflowAdvice: null,
  removedPrintSectionCount: 0,
}
import {
  addPupilScoreNote,
  deletePupilScoreNote,
  noteTypeMapping,
  updatePupilScoreNote,
} from "entities/pupils/pupils.actions"

/*
 *
 * newObject - object containing key and other properties
 * currentList - list containing objects with key and other properties
 *
 * Based on the key either add or update newObject
 * in currentList.
 *
 */
export const upsertObjectInList = (newObject, currentList, uniquePath) => {
  if (!uniquePath) {
    uniquePath = ["id"]
  }

  if (!newObject || !path(uniquePath)(newObject)) {
    return currentList
  }

  let newList
  if (
    currentList.find(
      (currentObject) =>
        path(uniquePath)(currentObject) === path(uniquePath)(newObject),
    )
  ) {
    newList = currentList.map((currentObject) =>
      path(uniquePath)(currentObject) === path(uniquePath)(newObject)
        ? newObject
        : currentObject,
    )
  } else {
    newList = [...currentList, newObject]
  }
  return newList
}

export default createReducer(initialState, (builder) => {
  builder
    .addCase("GET_PUPIL_REQUEST", (state) => ({
      ...state,
      isPupilLoading: true,
    }))
    .addCase("GET_PUPIL_SUCCESS", (state, action) => {
      if (!action.payload || !action.payload.id) {
        return { ...state, isPupilLoading: false }
      }
      return {
        ...state,
        isPupilLoading: false,
        currentPupil: action.payload,
      }
    })
    .addCase("GET_PUPIL_FAILURE", (state) => ({
      ...state,
      isPupilLoading: false,
    }))

    .addCase("UPDATE_PUPIL_OBSERVATION_SUCCESS", (state, action) => {
      if (!state.currentPupil) {
        return state
      }
      const observations = upsertObjectInList(
        action.payload,
        state.currentPupil.observations,
      )

      return {
        ...state,
        currentPupil: {
          ...state.currentPupil,
          observations,
        },
      }
    })

    .addCase(addPupilScoreNote, (state, action) =>
      handleNoteUpsert(state, action),
    )
    .addCase(updatePupilScoreNote, (state, action) =>
      handleNoteUpsert(state, action),
    )
    .addCase(deletePupilScoreNote, (state, action) =>
      handleNoteDelete(state, action),
    )
    .addCase("INCREMENT_REMOVED_PRINT_SECTION_COUNT", (state) => {
      return {
        ...state,
        removedPrintSectionCount: state.removedPrintSectionCount + 1,
      }
    })
    .addCase("DECREMENT_REMOVED_PRINT_SECTION_COUNT", (state) => {
      return {
        ...state,
        removedPrintSectionCount:
          state.removedPrintSectionCount > 0
            ? state.removedPrintSectionCount - 1
            : 0,
      }
    })
    .addCase("RESET_REMOVED_PRINT_SECTION_COUNT", (state) => {
      return {
        ...state,
        removedPrintSectionCount: initialState.removedPrintSectionCount,
      }
    })

    .addCase("GET_PUPIL_OUTFLOW_ADVICE_REQUEST", (state) => {
      return {
        ...state,
        pupilOutflowAdvice: undefined,
        isPupilOutflowAdviceLoading: true,
      }
    })
    .addCase("GET_PUPIL_OUTFLOW_ADVICE_SUCCESS", (state, action) => {
      return {
        ...state,
        pupilOutflowAdvice: action.payload,
        isPupilOutflowAdviceLoading: false,
      }
    })
    .addCase("GET_PUPIL_OUTFLOW_ADVICE_FAILURE", (state) => {
      return {
        ...state,
        isPupilOutflowAdviceLoading: false,
      }
    })

    .addCase("UPDATE_PUPIL_OUTFLOW_ADVICE_REQUEST", (state) => {
      return {
        ...state,
        isPupilOutflowAdviceLoading: true,
      }
    })
    .addCase("UPDATE_PUPIL_OUTFLOW_ADVICE_SUCCESS", (state, action) => {
      return {
        ...state,
        pupilOutflowAdvice: action.payload,
        isPupilOutflowAdviceLoading: false,
      }
    })
    .addCase("UPDATE_PUPIL_OUTFLOW_ADVICE_FAILURE", (state) => {
      return {
        ...state,
        isPupilOutflowAdviceLoading: false,
      }
    })
})

const UPSERT_ACTION_TYPE = "UPSERT_ACTION_TYPE"
const DELETE_ACTION_TYPE = "DELETE_ACTION_TYPE"

/*
 * This is a generic function which searches for the notes depending on
 * the noteType and either inserts/updates (upsert) them or deletes them
 * depending on the actionToApplyOnNotes parameter.
 *
 * state - store slice
 * action - action with payload
 * noteType - specifies which noteType to use from the noteTypeMapping
 * actionToApplyOnNotes - specifies upsert or delete to notes
 *
 */
const handleNoteAction = (state, action, actionToApplyOnNotes) => {
  if (!state.currentPupil) {
    return state
  }

  const { testType, note } = action.payload

  /*
   *
   * currentPupil state looks like:
   {
    ...

     scores: [
     ^^^^^^
   scoresStoreKey

      {
        ...
        id: 20665500,
        roman: 'IV'
        notes: [
         {
           id: 3170046,
               ^^^^^^^
               note.id


           citoTestScoreId: 20665500,
           ^^^^^^^^^^^^^^^^
             scoreIdKey
           ...
         },
        ]
      },
      ]
    }
    */

  //scoresStoreKey refers to e.g., scores/methodScore in the currentPupil store
  //scoreIdKey refers to e.g., citoTestScoreId/methodTestScoreId in the note payload
  const { scoresStoreKey, scoreIdKey } = noteTypeMapping[testType]

  let scores = path(["currentPupil", scoresStoreKey])(state)
  if (!scores) {
    return state
  }
  let score = find(propEq(note[scoreIdKey], "id"))(scores)
  let getScoreByParentId = false
  if (!score) {
    // fallback to search for parent id when score does not have its own id
    score = find((score) => {
      return !score.id && score.parent && score.parent.id === note[scoreIdKey]
    })(scores)
    getScoreByParentId = true
  }
  if (!score) {
    return state
  }

  let newNotes
  if (actionToApplyOnNotes === UPSERT_ACTION_TYPE) {
    newNotes = upsertObjectInList(note, score.notes)
  } else if (actionToApplyOnNotes === DELETE_ACTION_TYPE) {
    newNotes = reject(propEq(note.id, "id"), score.notes)
  }
  const newScore = { ...score, notes: newNotes }

  let uniquePath
  if (getScoreByParentId) {
    uniquePath = ["parent", "id"]
  } else {
    uniquePath = ["id"]
  }
  scores = upsertObjectInList(newScore, scores, uniquePath)
  return {
    ...state,
    currentPupil: {
      ...state.currentPupil,
      [scoresStoreKey]: scores,
    },
  }
}

const handleNoteUpsert = (state, action) => {
  return handleNoteAction(state, action, UPSERT_ACTION_TYPE)
}

const handleNoteDelete = (state, action) => {
  return handleNoteAction(state, action, DELETE_ACTION_TYPE)
}
